import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
	Backdrop,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	IconButton,
	Paper,
	TextField,
	Tooltip,
	Typography,
	Alert,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { Loader } from "../../shared";
import ChangesFormActions from "../../shared/formChanges/ChangesFormActions";
import { CustomTextField } from "../../shared/CustomTextField";
import FileDropzone from "../../FileDropzone";
import { knowledgeDocumentService } from "../../../services/knowledgeDocuments";
import { KnowledgeDocumentDto } from "../../../types/knowledgeDocument";
import { MenuRoutes } from "../../../types/routes";
import { CCMAXLENGTHS, HTTPSTATUSCODES } from "../../../utils/constants";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
import bytesToSize from "../../../utils/bytesToSize";

const ACCEPTED_FORMATS = ".pdf,.txt,.docx,.md";
const MAX_FILE_SIZE_MB = 10;

interface UploadedFile {
	documentId: string;
	fileName: string;
	fileSize: number; // bytes
}

const AddEditKnowledgeDocument: FC = () => {
	const { documentId } = useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isMountedRef = useIsMountedRef();
	const { enqueueSnackbar } = useSnackbar();

	const [document, setDocument] = useState<KnowledgeDocumentDto | null>(null);
	const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(!documentId);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isUploading, setIsUploading] = useState<boolean>(false);

	// Newly uploaded file (upload happens immediately on drop)
	const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
	const uploadedFileRef = useRef<UploadedFile | null>(null);
	const previousUploadedFile = useRef<UploadedFile | null>(null);
	const formikRef = useRef<FormikProps<{ name: string; description: string }>>(null);

	// Tracks whether the existing file (edit mode) was removed
	const [existingFileRemoved, setExistingFileRemoved] = useState<boolean>(false);
	const [isRemoveFileDialogOpen, setIsRemoveFileDialogOpen] = useState<boolean>(false);

	useEffect(() => {
		if (documentId) {
			loadDocument();
		}
	}, [documentId]);

	useEffect(() => {
		return () => {
			if (uploadedFileRef.current) {
				deleteFromStorage(uploadedFileRef.current);
			}
		};
	}, []);

	useEffect(() => {
		uploadedFileRef.current = uploadedFile;
	}, [uploadedFile]);

	const loadDocument = async () => {
		try {
			const response = await knowledgeDocumentService.getKnowledgeDocumentById(documentId);
			if (isMountedRef.current && response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setDocument(response.data);
				setDownloadUrl(response.data.downloadUrl ?? null);
				setIsDataLoaded(true);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const onDrop = (acceptedFiles: File[]) => {
		if (acceptedFiles.length === 0) { 
			return;
		}

		const file = acceptedFiles[0];
		if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
			enqueueSnackbar(`Max file size is ${MAX_FILE_SIZE_MB} MB`, { variant: "error" });

			return;
		}

		handleUploadToStorage(file);
	};

	const handleUploadToStorage = async (file: File) => {
		const newDocumentId = documentId ?? (crypto as unknown as { randomUUID: () => string }).randomUUID();
		const formData = new FormData();
		formData.append("file", file, file.name);

		try {
			setIsUploading(true);
			const response = await knowledgeDocumentService.uploadDocument(newDocumentId, formData);
			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				if (previousUploadedFile.current) {
					deleteFromStorage(previousUploadedFile.current);
				}
				previousUploadedFile.current = uploadedFile;
				setUploadedFile({ documentId: newDocumentId, fileName: file.name, fileSize: file.size });
			}
		} catch (error) {
			console.error(error);
			enqueueSnackbar(t("KnowledgeDocument.UploadFailed"), { variant: "error" });
		} finally {
			setIsUploading(false);
		}
	};

	const deleteFromStorage = async (file: UploadedFile) => {
		try {
			await knowledgeDocumentService.deleteUploadedDocument(file.documentId, file.fileName);
		} catch (error) {
			console.error(error);
		}
	};

	const handleRemoveUploadedFile = () => {
		if (uploadedFile) {
			deleteFromStorage(uploadedFile);
		}
		previousUploadedFile.current = null;
		setUploadedFile(null);
	};

	const handleRemoveExistingFile = () => {
		setIsRemoveFileDialogOpen(true);
	};

	const handleRemoveExistingFileConfirm = () => {
		setIsRemoveFileDialogOpen(false);
		setExistingFileRemoved(true);
	};

	const handleCancel = () => {
		if (documentId) {
			const hasChanges = formikRef.current?.dirty || !!uploadedFile || existingFileRemoved;

			if (hasChanges) {
				formikRef.current?.resetForm();
				if (uploadedFile) {
					deleteFromStorage(uploadedFile);
					uploadedFileRef.current = null;
					previousUploadedFile.current = null;
					setUploadedFile(null);
				}
				setExistingFileRemoved(false);
				return;
			}
		} else if (uploadedFile) {
			deleteFromStorage(uploadedFile);
			uploadedFileRef.current = null;
		}

		navigate(MenuRoutes.Knowledge);
	};

	const handleSubmit = async (values: { name: string; description: string }) => {
		const isCreate = !documentId;

		if (!uploadedFile && (isCreate || existingFileRemoved)) {
			enqueueSnackbar(t("KnowledgeDocument.FileRequired"), { variant: "error" });

			return;
		}

		setIsSubmitting(true);
		try {
			const payload: KnowledgeDocumentDto = {
				id: isCreate ? uploadedFile.documentId : documentId,
				name: values.name,
				description: values.description ?? "",
				fileName: isCreate ? uploadedFile.fileName : document?.fileName ?? "",
				uploadedFileName: uploadedFile ? uploadedFile.fileName : null,
				fileSize: uploadedFile ? uploadedFile.fileSize : document?.fileSize ?? 0,
				agentNames: [],
			};

			const response = documentId
				? await knowledgeDocumentService.updateKnowledgeDocument(documentId, payload)
				: await knowledgeDocumentService.createKnowledgeDocument(payload);

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				uploadedFileRef.current = null;
				enqueueSnackbar(
					t(documentId ? "KnowledgeDocument.UpdateSucceeded" : "KnowledgeDocument.CreateSucceeded"),
					{ variant: "success" }
				);
				navigate(MenuRoutes.Knowledge);
			}
		} catch {
			enqueueSnackbar(
				t(documentId ? "KnowledgeDocument.UpdateFailed" : "KnowledgeDocument.CreateFailed"),
				{ variant: "error" }
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const isSyncInProgress = document?.isAnySyncInProgress === true;
	const showDropzone = !uploadedFile && (!documentId || !document?.fileName || existingFileRemoved);
	const showUploadedFile = !!uploadedFile;
	const showExistingFile = !!documentId && !!document?.fileName && !existingFileRemoved && !uploadedFile;

	return (
		<>
			<Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isUploading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Loader isOpen={!isDataLoaded} isInline={false} />
			{isDataLoaded && (
				<Card sx={{ pl: 3, pb: 4, pr: 3, pt: 4 }}>
					<Formik
						innerRef={formikRef}
						initialValues={{
							name: document?.name ?? "",
							description: document?.description ?? "",
						}}
						validationSchema={Yup.object().shape({
							name: Yup.string()
								.required(t("KnowledgeDocument.NameRequired"))
								.max(CCMAXLENGTHS.KnowledgeDocumentName, t("KnowledgeDocument.NameTooLong")),
							description: Yup.string()
								.max(CCMAXLENGTHS.KnowledgeDocumentDescription, t("KnowledgeDocument.DescriptionTooLong")),
						})}
						onSubmit={handleSubmit}
					>
						{({ errors, handleBlur, handleChange, handleSubmit: formikSubmit, touched, values, isValid }) => (
							<form onSubmit={formikSubmit}>
								<ChangesFormActions
									isValid={true}
									isSubmitting={isSubmitting}
									disableSave={isSyncInProgress}
									handleReset={handleCancel}
									isSaveAction={false}
								/>
								<Grid container spacing={3}>
									{isSyncInProgress && (
										<Grid item xs={12}>
											<Alert severity="info">
												{t("KnowledgeDocument.SyncInProgressDisabled")}
											</Alert>
										</Grid>
									)}
									{/* Name */}
									<Grid item xs={12} md={6}>
										<CustomTextField
											fullWidth
											size="small"
											name="name"
											placeholder={t("KnowledgeDocument.EnterDocumentName")}
											label={`${t("KnowledgeDocument.Name")} *`}
											value={values.name}
											onChange={handleChange}
											onBlur={handleBlur}
											error={Boolean(touched.name && errors.name)}
											helperText={
												(touched.name && errors.name)}
											disabled={isSyncInProgress}
										/>
									</Grid>

									{/* File Upload */}
									<Grid item xs={12} md={6} />
									<Grid item xs={12} md={6}>
										{showDropzone && !isSyncInProgress && (
											<>
												<FileDropzone
													files={[]}
													onDrop={onDrop}
													accept={ACCEPTED_FORMATS}
													maxFiles={1}
												/>
												<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
													{t("KnowledgeDocument.SupportedFormats")}
												</Typography>
											</>
										)}

										{showUploadedFile && (
											<FileTile
												fileName={uploadedFile.fileName}
												fileSize={uploadedFile.fileSize}
												onRemove={handleRemoveUploadedFile}
											/>
										)}

										{showExistingFile && (
											<FileTile
												fileName={document.fileName}
												fileSize={document.fileSize * 1024}
												onRemove={handleRemoveExistingFile}
												downloadUrl={downloadUrl}
												disableRemove={isSyncInProgress}
											/>
										)}
									</Grid>
									<Grid item xs={12} md={6} />

									

									{/* Short Description */}
									{(showUploadedFile || showExistingFile) && (
										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												multiline
												rows={6}
												size="small"
												name="description"
												label={t("KnowledgeDocument.ShortDescription")}
												value={values.description}
												onChange={handleChange}
												onBlur={handleBlur}
												error={Boolean(touched.description && errors.description)}
												helperText={
													(touched.description && errors.description)}
												disabled={isSyncInProgress}
											/>
										</Grid>
									)}
									{/* Used by AI Agents (edit mode only) */}
									{documentId && document?.agentNames?.length > 0 && (
										<Grid item xs={12}>
											<Typography variant="subtitle2" gutterBottom>
												{t("KnowledgeDocument.UsedByFollowingAgents")}
											</Typography>
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
												{document.agentNames.map((name) => (
													<Chip
														key={name}
														label={name}
																			/>
												))}
											</Box>
										</Grid>
									)}
								</Grid>
							</form>
						)}
					</Formik>
				</Card>
			)}
			<Dialog open={isRemoveFileDialogOpen} onClose={() => setIsRemoveFileDialogOpen(false)}>
				<DialogTitle>{t("KnowledgeDocument.RemoveFileConfirmTitle")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t("KnowledgeDocument.RemoveFileConfirmMessage")}
					</DialogContentText>
					{document?.agentNames?.length > 0 && (
						<DialogContentText sx={{ mt: 1 }}>
							{t("KnowledgeDocument.DeleteUsedByAgents")}
						</DialogContentText>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsRemoveFileDialogOpen(false)}>
						{t("Shared.Cancel")}
					</Button>
					<Button onClick={handleRemoveExistingFileConfirm} color="error" variant="contained">
						{t("Shared.Delete")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

interface FileTileProps {
	fileName: string;
	fileSize?: number;
	onRemove: () => void;
	downloadUrl?: string | null;
	disableRemove?: boolean;
}

const FileTile: FC<FileTileProps> = ({ fileName, fileSize, onRemove, downloadUrl, disableRemove }) => {

	const { t } = useTranslation();

	return (
		<Paper
			variant="outlined"
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				p: 1.5,
				borderRadius: 2,
			}}
		>
			<Box
				sx={{
					width: 40,
					height: 40,
					borderRadius: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				<DescriptionIcon sx={{ color: "primary.main", fontSize: 22 }} />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography variant="subtitle2" noWrap>
					{fileName}
				</Typography>
				{fileSize !== undefined && (
					<Typography variant="caption" color="text.secondary">
						{bytesToSize(fileSize)}
					</Typography>
				)}
			</Box>
			{downloadUrl && (
				<Tooltip title="Download">
					<IconButton size="small" href={downloadUrl} download>
						<DownloadIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
			<Tooltip title={disableRemove ? t("KnowledgeDocument.SyncInProgressDisabled") : "Remove"}>
				<span>
					<IconButton size="small" onClick={onRemove} disabled={disableRemove}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		</Paper>
	);
};

export default AddEditKnowledgeDocument;
